import React, { useLayoutEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { getFabVisualConfig } from '../utils/fabVisualConfig';

const AMBIENT = {
  skyTop: '#F8FAFC',
  skyGround: '#DCEAF4',
  ambient: '#F2F6FA',
  key: '#FFFFFF',
  fill: '#F5F8FB',
  rim: '#E2E8F0',
} as const;

export type FabLightingProps = {
  center?: [number, number, number];
  span?: number;
  castShadow?: boolean;
};

/** High-end industrial cleanroom lighting with balanced key, soft fill, rim highlights, and soft shadows. */
export const FabLighting: React.FC<FabLightingProps> = ({
  center = [0, 0, 0],
  span = 4000,
  castShadow = true,
}) => {
  const { gl } = useThree();
  const keyRef = useRef<THREE.DirectionalLight>(null);
  const [cx, , cz] = center;
  const config = getFabVisualConfig();
  const shadowsOn = castShadow && config.enableShadows;

  useLayoutEffect(() => {
    if (shadowsOn) {
      gl.shadowMap.enabled = true;
      gl.shadowMap.type = THREE.PCFSoftShadowMap;
      gl.shadowMap.needsUpdate = true;
    }
  }, [gl, shadowsOn, cx, cz, span]);

  useLayoutEffect(() => {
    const light = keyRef.current;
    if (!light || !shadowsOn) return;
    light.shadow.mapSize.set(2048, 2048);
    light.shadow.camera.near = span * 0.01;
    light.shadow.camera.far = span * 3;
    const half = span * 0.55;
    light.shadow.camera.left = -half;
    light.shadow.camera.right = half;
    light.shadow.camera.top = half;
    light.shadow.camera.bottom = -half;
    light.shadow.bias = -0.00015;
    light.shadow.normalBias = 0.018;
    gl.shadowMap.needsUpdate = true;
  }, [gl, span, shadowsOn]);

  return (
    <group name="FabLighting">
      {/* Cool cleanroom ceiling + floor bounce hemisphere */}
      <hemisphereLight args={[AMBIENT.skyTop, AMBIENT.skyGround, 0.42]} />
      {/* Soft ambient floor illumination */}
      <ambientLight intensity={0.20} color={AMBIENT.ambient} />
      {/* Key directional sunlight for primary form definition, clean specular highlights and crisp shadows */}
      <directionalLight
        ref={keyRef}
        position={[cx + span * 0.12, span * 0.72, cz - span * 0.16]}
        intensity={0.82}
        color={AMBIENT.key}
        castShadow={shadowsOn}
      />
      {/* Soft fill light preventing pitch-black machine undersides without washing out contrasts */}
      <directionalLight
        position={[cx - span * 0.18, span * 0.36, cz + span * 0.22]}
        intensity={0.28}
        color={AMBIENT.fill}
      />
      {/* Subtle rim light accentuating machine shoulders and rail edges */}
      <directionalLight
        position={[cx - span * 0.14, span * 0.50, cz - span * 0.26]}
        intensity={0.20}
        color={AMBIENT.rim}
      />
    </group>
  );
};

export default FabLighting;
